<script lang="ts">
import AppSelect from '@/components/v2/AppSelect.vue';

export default {
  name: 'DocumentUpload',
  components: {
    AppSelect,
  },
};

interface Document {
  name: string;
  type: DocumentType;
  accountId: number;
  programId?: number;
  policyId?: number;
  policyNumber?: string;
  file: File;
  selected: boolean;
  readyForUpload: boolean;
  tags?: string[];
}

interface ProgramOption extends ProgramDetails {
  selected: boolean;
}

interface Options {
  documentTypes: DocumentType[];
  currentPrograms: ProgramOption[];
  currentProgramsLoaded: boolean;
  currentProgramsLoading: boolean;
  expiredPrograms: ProgramOption[];
  expiredProgramsLoaded: boolean;
  expiredProgramsLoading: boolean;
}

interface SelectedOptions {
  documentType?: DocumentType;
  programId?: number;
  policyId?: number;
  policyNumber?: string;
}
</script>

<script setup lang="ts">
import { ref, reactive, watch, computed } from 'vue';
import {
  CloudUpload,
  Trash2,
  ChevronsRight,
  ChevronsLeft,
  OctagonAlert,
  CircleCheck,
  CircleX,
  X,
} from 'lucide-vue-next';
import { DocumentDomainTypeClientUploaded, DocumentType } from '@/types/v2/Document';
import { documentService } from '@/services/v2/DocumentService';
import { accountService } from '@/services/api';
import { ProgramDetails } from '@/types/Programs';
import { Account } from '@/types/Account';
import { EpicPolicy } from '@/types/tower/Domain';
import { useRouter } from 'vue-router';
import { IsUserAClient } from '@/utilities/AuthUtils.js';
import Store from '@/store/index.js';
import { TabsContent, TabsList, TabsRoot, TabsTrigger } from 'reka-ui';
import Combobox, { type ComboboxOption } from '@/components/v2/Combobox.vue';
import { escapeHtml, sanitizeHighlight } from '@/utilities/Sanitization';

const props = defineProps<{
  account: Account;
}>();

const router = useRouter();

// -----
// refs
// -----

const documentsSectionRef = ref<HTMLElement | null>(null);
const documentTypesSectionRef = ref<HTMLElement | null>(null);
const programsAndPoliciesSectionRef = ref<HTMLElement | null>(null);
const tagsSectionRef = ref<HTMLElement | null>(null);

const documents = ref([] as Partial<Document>[]);
const selectedOptions = reactive<SelectedOptions>({});
const showCurrentProgramsOnly = ref(true);
const options = reactive<Options>({
  documentTypes: [],
  currentPrograms: [],
  currentProgramsLoaded: false,
  currentProgramsLoading: false,
  expiredPrograms: [],
  expiredProgramsLoaded: false,
  expiredProgramsLoading: false,
});

const draggingOver = ref(false);
const fileInput = ref<HTMLInputElement | null>(null);
const allAccountPolicies = ref<EpicPolicy[]>([]);
const allAccountPoliciesLoading = ref(false);
const currentProgramPoliciesTab = ref<string>('programs');
const showCurrentPoliciesOnly = ref(true);
const policySearchQuery = ref('');
const uploading = ref<boolean>(false);
const availableTags = ref<ComboboxOption[]>([]);
const selectedTags = ref<ComboboxOption[]>([]);
const selectedTag = ref<string>('');
const isUpdatingTags = ref<boolean>(false);

// ---------
// computed
// ---------

const selectedDocuments = computed(() => {
  return documents.value.filter((document) => document.selected);
});

const accountDocumentTypes = computed(() => {
  return options.documentTypes
    .filter((documentType) => documentType.domainName === 'Account')
    .sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
});

const policyDocumentTypes = computed(() => {
  return options.documentTypes
    .filter((documentType) => documentType.domainName === 'Policy')
    .sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
});

const programs = computed(() => {
  if (showCurrentProgramsOnly.value) {
    return options.currentPrograms;
  } else {
    return options.expiredPrograms;
  }
});

const programsLoaded = computed(() => {
  return showCurrentProgramsOnly.value ? options.currentProgramsLoaded : options.expiredProgramsLoaded;
});

const policiesFromSelectedProgram = computed(() => {
  if (!selectedOptions.programId) return [];

  if (showCurrentProgramsOnly.value) {
    const program = options.currentPrograms.find((program) => program.program.id === selectedOptions.programId);
    return program?.policies ?? [];
  } else {
    const program = options.expiredPrograms.find((program) => program.program.id === selectedOptions.programId);
    return program?.policies ?? [];
  }
});

const readyForUpload = computed(() => {
  return documents.value.every((document) => document.readyForUpload);
});

const isClientRole = computed(() => {
  return IsUserAClient(Store.state.user);
});

const clientUploadedDocumentTypes = computed(() => {
  return options.documentTypes
    .filter((documentType) => documentType.domainName === DocumentDomainTypeClientUploaded)
    .sort((a, b) => a.name.localeCompare(b.name));
});

const filteredAccountPolicies = computed(() => {
  if (!policySearchQuery.value.trim()) {
    return allAccountPolicies.value;
  }

  const searchTerm = policySearchQuery.value.toLowerCase().trim();
  return allAccountPolicies.value.filter((policy) => {
    return (
      policy.description?.toLowerCase().includes(searchTerm) ||
      policy.policyID?.toLowerCase().includes(searchTerm) ||
      policy.carrierGroup?.toLowerCase().includes(searchTerm) ||
      policy.coverageType?.toLowerCase().includes(searchTerm)
    );
  });
});

const availableTagsFiltered = computed(() => {
  return availableTags.value.filter(tag => 
    !selectedTags.value.some(selectedTag => selectedTag.value === tag.value)
  );
});

const tagsComboboxKey = computed(() => {
  // Create a unique key based on selected document indices to force Combobox remount
  return documents.value
    .map((doc, index) => doc.selected ? index : null)
    .filter(idx => idx !== null)
    .join('-') || 'none';
});

// ---------
// watchers
// ---------

watch(showCurrentProgramsOnly, async () => {
  selectedOptions.programId = undefined;
  selectedOptions.policyId = undefined;
  selectedOptions.policyNumber = undefined;
  await loadPrograms();
});

watch(currentProgramPoliciesTab, async (newTab) => {
  if (newTab === 'policies') {
    await loadAllAccountPolicies();
  }
});

watch(showCurrentPoliciesOnly, async () => {
  if (currentProgramPoliciesTab.value === 'policies') {
    await loadAllAccountPolicies();
  }
});

watch(documents, async (newDocuments) => {
  if (newDocuments.length > 0) {
    addEventListener('click', handleClickOutside);

    if (isClientRole.value) {
      await loadDocumentTypes();

      // client document uploads only have 1 type and is automatically assigned

      documents.value.forEach((document) => {
        document.type = undefined;
      });

      validateDocuments();
    } else {
      await Promise.all([loadDocumentTypes(), loadPrograms()]);
    }

    // Load tags if not already loaded
    if (availableTags.value.length === 0) {
      await getTags();
    }
  } else {
    removeEventListener('click', handleClickOutside);
  }
});

watch(selectedDocuments, (newSelectedDocuments) => {
  // Always clear the input when selection changes
  selectedTag.value = '';

  // no documents are selected
  if (newSelectedDocuments.length === 0) {
    selectedOptions.documentType = undefined;
    selectedOptions.policyId = undefined;
    selectedOptions.programId = undefined;
    selectedTags.value = [];
  }

  // only one document is selected
  else if (newSelectedDocuments.length === 1) {
    selectedOptions.documentType = newSelectedDocuments[0]?.type;
    selectedOptions.policyId = newSelectedDocuments[0]?.policyId;
    selectedOptions.programId = newSelectedDocuments[0]?.programId;
    // Load tags from the selected document
    selectedTags.value = (newSelectedDocuments[0]?.tags || []).map(tag => ({
      label: tag,
      value: tag
    }));
  }

  // multiple documents are selected
  else if (newSelectedDocuments.length > 1) {
    // if they all have the same document type, set the selected document type
    if (
      newSelectedDocuments[0]?.type &&
      newSelectedDocuments.reduce((acc, document) => acc && document?.type === newSelectedDocuments[0]?.type, true)
    ) {
      selectedOptions.documentType = newSelectedDocuments[0].type;
    } else {
      selectedOptions.documentType = undefined;
    }

    // if they all have the same policy, set the selected policy
    if (
      newSelectedDocuments[0]?.policyId &&
      newSelectedDocuments.reduce(
        (acc, document) => acc && document?.policyId === newSelectedDocuments[0]?.policyId,
        true,
      )
    ) {
      selectedOptions.policyId = newSelectedDocuments[0].policyId;
      selectedOptions.programId = newSelectedDocuments[0].programId;
    }

    // if they have different document types, clear the selected options
    else {
      selectedOptions.policyId = undefined;
      selectedOptions.programId = undefined;
    }

    // Get intersection of tags from all selected documents
    const firstDocTags = newSelectedDocuments[0]?.tags || [];
    const commonTags = firstDocTags.filter(tag => 
      newSelectedDocuments.every(doc => doc.tags?.includes(tag))
    );
    selectedTags.value = commonTags.map(tag => ({
      label: tag,
      value: tag
    }));
  }
});

// ---------------------
// functions & handlers
// ---------------------

function handleFileDragOver(e: DragEvent) {
  e.preventDefault();
  e.stopPropagation();
  draggingOver.value = true;
}

function handleFileDrop(e: DragEvent) {
  e.preventDefault();
  e.stopPropagation();
  draggingOver.value = false;

  if (e.dataTransfer?.items) {
    // use the DataTransferItemList interface if available
    let droppedDocuments: Partial<Document>[] = [];
    [...e.dataTransfer.items].forEach((item) => {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          droppedDocuments.push({
            name: file.name,
            file: file,
            tags: [],
          });
        }
      }
    });
    documents.value = [...documents.value, ...droppedDocuments];
  } else if (e.dataTransfer?.files) {
    // otherwise use the DataTransfer interface
    let droppedDocuments: Partial<Document>[] = [];
    [...e.dataTransfer.files].forEach((file) => {
      droppedDocuments.push({
        name: file.name,
        file: file,
        tags: [],
      });
    });
    documents.value = [...documents.value, ...droppedDocuments];
  }

  // select the document if only one was dropped
  if (documents.value.length === 1) {
    documents.value[0].selected = true;
  }
}

function handleFileDragLeave() {
  draggingOver.value = false;
}

function handleSelectFiles() {
  if (fileInput.value) {
    fileInput.value.click();
  }
}

function handleFilesSelected(e: Event) {
  const target = e.target as HTMLInputElement;
  if (target.files) {
    const selectedDocuments = [...target.files].map((file) => ({
      name: file.name,
      file: file,
      tags: [],
    }));
    documents.value = [...documents.value, ...selectedDocuments];

    // select the document if only one was selected
    if (documents.value.length === 1) {
      documents.value[0].selected = true;
    }
  }

  e.preventDefault();
  e.stopPropagation();
}

function handleDeleteDocument(documentIndex: number) {
  documents.value = documents.value.filter((_, index) => index !== documentIndex);
}

function handleSelectDocumentType(documentType: DocumentType) {
  if (selectedOptions.documentType === documentType) {
    selectedOptions.documentType = undefined;
  } else {
    selectedOptions.documentType = documentType;
  }

  selectedDocuments.value.map((document) => {
    document.type = selectedOptions.documentType;
  });

  validateDocuments();
}

function handleSelectProgram(programId: number) {
  if (selectedOptions.programId === programId) {
    selectedOptions.programId = undefined;
  } else {
    selectedOptions.programId = programId;
  }
}

function handleSelectPolicy(policy: EpicPolicy) {
  if (!policy || !policy.uniqPolicy) return;

  if (selectedOptions.policyId === Number(policy.uniqPolicy)) {
    selectedOptions.policyId = undefined;
    selectedOptions.policyNumber = undefined;
  } else {
    selectedOptions.policyId = Number(policy.uniqPolicy);
    selectedOptions.policyNumber = policy.policyID;
  }

  selectedDocuments.value.map((document) => {
    document.policyId = selectedOptions.policyId;
    document.policyNumber = selectedOptions.policyNumber;
    document.programId = selectedOptions.programId;
  });

  validateDocuments();
}

function validateDocuments() {
  documents.value.forEach((document) => {
    if (!document.type) {
      document.readyForUpload = false;
    } else if (isPolicyDocumentType(document.type) && !document.policyId) {
      document.readyForUpload = false;
    } else {
      document.readyForUpload = true;
    }
  });
}

function handleClickOutside(event: MouseEvent) {
  const target = event.target as HTMLElement;
  
  // Check if click is inside a combobox dropdown (which is rendered in a portal)
  const isInsideComboboxDropdown = target.closest('[role="listbox"]') !== null;
  
  if (
    documentsSectionRef.value &&
    !documentsSectionRef.value.contains(event.target as Node) &&
    documentTypesSectionRef.value &&
    !documentTypesSectionRef.value.contains(event.target as Node) &&
    programsAndPoliciesSectionRef.value &&
    !programsAndPoliciesSectionRef.value.contains(event.target as Node) &&
    tagsSectionRef.value &&
    !tagsSectionRef.value.contains(event.target as Node) &&
    !isInsideComboboxDropdown
  ) {
    documents.value = documents.value.map((document) => ({ ...document, selected: false }));
  }
}

function handleCancel() {
  router.back();
}

async function handleUpload() {
  if (!readyForUpload.value) return;

  uploading.value = true;

  const requests = documents.value.map((document) => {
    return documentService.uploadDocumentForPolicyOrAccount({
      domain: document.type!.domainName,
      type: document.type!.name,
      displayName: document.name,
      fileName: document.name!,
      accountId: Number(props.account.accountID),
      policyId: document.policyId,
      document: document.file as File,
      tags: document.tags && document.tags.length > 0 ? document.tags : undefined,
    });
  });

  try {
    await Promise.all(requests);
    await router.replace(`/account/${props.account.accountID}/documents`);
  } catch (e) {
    console.error(e);
  }

  uploading.value = false;
}

function handleRemoveDocumentType(document: Partial<Document>) {
  selectedOptions.documentType = undefined;
  document.type = undefined;
  validateDocuments();
}

function handleRemovePolicy(document: Partial<Document>) {
  selectedOptions.policyId = undefined;
  selectedOptions.programId = undefined;
  selectedOptions.policyNumber = undefined;
  document.programId = undefined;
  document.policyId = undefined;
  document.policyNumber = undefined;
  validateDocuments();
}

function isPolicyDocumentType(documentType: DocumentType): boolean {
  return policyDocumentTypes.value.some((type) => type === documentType);
}

function highlightText(text: string | undefined, searchQuery: string): string {
  if (!text || !searchQuery.trim()) {
    return escapeHtml(text || '');
  }

  // Escape the text to prevent XSS
  const escapedText = escapeHtml(text);
  const searchTerm = searchQuery.trim();
  const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const highlighted = escapedText.replace(regex, '<mark class="tw:bg-yellow-200 tw:px-0.5 tw:rounded">$1</mark>');
  
  return sanitizeHighlight(highlighted);
}

async function loadDocumentTypes(): Promise<void> {
  if (options.documentTypes.length === 0) {
    options.documentTypes = await documentService.getDocumentTypes();
  }
}

async function loadPrograms(): Promise<void> {
  if (showCurrentProgramsOnly.value && !options.currentProgramsLoaded && !options.currentProgramsLoading) {
    options.currentProgramsLoading = true;
    const programs = await getProgramsForAccount(Number(props.account.accountID), false);
    options.currentPrograms = await loadProgramDetails(Number(props.account.accountID), programs);
    options.currentProgramsLoaded = true;
    options.currentProgramsLoading = false;
  } else if (!showCurrentProgramsOnly.value && !options.expiredProgramsLoaded && !options.expiredProgramsLoading) {
    options.expiredProgramsLoading = true;
    const programs = await getProgramsForAccount(Number(props.account.accountID), true);
    options.expiredPrograms = await loadProgramDetails(Number(props.account.accountID), programs);
    options.expiredProgramsLoaded = true;
    options.expiredProgramsLoading = false;
  }

  async function getProgramsForAccount(accountID: number, showExpired: boolean): Promise<ProgramOption[]> {
    return (await accountService.getProgramsForAccount(accountID, showExpired)).map((prog) => ({
      program: prog,
      policies: [],
      selected: false,
    }));
  }

  async function loadProgramDetails(accountID: number, programs: ProgramOption[]): Promise<ProgramOption[]> {
    const programDetails = await Promise.all(
      programs.map(async (program) => {
        return accountService.getProgramDetails(accountID, program.program.id, isClientRole.value);
      }),
    );

    programDetails.forEach((programDetail, index) => {
      programs[index].policies = programDetail.policies;
    });

    return programs;
  }
}

async function loadAllAccountPolicies(): Promise<void> {
  allAccountPoliciesLoading.value = true;
  try {
    allAccountPolicies.value = await accountService.policiesById(
      Number(props.account.accountID),
      !showCurrentPoliciesOnly.value, // include expired
      false, // withoutProgram
      false, // showExternal
      isClientRole.value,
    );
  } catch (error) {
    console.error('Failed to load account policies:', error);
    allAccountPolicies.value = [];
  } finally {
    allAccountPoliciesLoading.value = false;
  }
}

async function getTags() {
  try {
    const tags = await documentService.getDocumentTags();
    availableTags.value = (tags || []).map(tag => ({
      label: tag.name,
      value: tag.name
    }));
  } catch (error) {
    console.error('Failed to load tags:', error);
    availableTags.value = [];
  }
}

function handleTagSelect(tag: ComboboxOption) {
  isUpdatingTags.value = true;
  
  // Apply tag to all selected documents
  documents.value.forEach((document) => {
    if (document.selected) {
      if (!document.tags) {
        document.tags = [];
      }
      if (!document.tags.includes(tag.value)) {
        document.tags.push(tag.value);
      }
    }
  });
  
  // Update selectedTags to reflect the new state
  if (!selectedTags.value.some(selectedTag => selectedTag.value === tag.value)) {
    selectedTags.value = [...selectedTags.value, tag];
  }
  
  // Clear the selection after adding
  selectedTag.value = '';
  
  isUpdatingTags.value = false;
}

function handleTagRemove(tag: ComboboxOption) {
  isUpdatingTags.value = true;
  
  // Remove tag from all selected documents
  documents.value.forEach((document) => {
    if (document.selected && document.tags) {
      document.tags = document.tags.filter(t => t !== tag.value);
    }
  });
  
  // Update selectedTags to reflect the new state
  selectedTags.value = selectedTags.value.filter(selectedTag => selectedTag.value !== tag.value);
  
  isUpdatingTags.value = false;
}
</script>

<template>
  <div class="tw:h-full">
    <div
      v-if="documents.length == 0"
      class="tw:flex tw:h-full tw:w-full tw:flex-col tw:items-center tw:gap-4 tw:px-5 tw:py-6"
      @dragover="handleFileDragOver"
      @drop="handleFileDrop"
      @dragleave="handleFileDragLeave">
      <a
        class="tw:btn-primary/80 tw:btn tw:flex tw:gap-1 tw:self-start tw:border-none tw:p-1 tw:px-2 tw:text-gray-100 tw:btn-sm"
        @click="handleCancel">
        <ChevronsLeft :size="20" />
        <span>Go Back</span>
      </a>
      <div
        class="tw:h-96 tw:w-full tw:max-w-7xl tw:rounded-lg tw:border-2 tw:border-dashed tw:border-gray-400 tw:transition-all"
        :class="{ 'tw:bg-blue-100': draggingOver }">
        <div class="tw:flex tw:h-full tw:flex-col tw:items-center tw:justify-center">
          <CloudUpload :size="55" class="tw:text-gray-400" />
          <div class="tw:text-2xl tw:font-bold tw:text-gray-400">Drag and drop files here</div>
          <div class="tw:text-xl tw:text-gray-400">or</div>
          <input ref="fileInput" type="file" multiple class="tw:hidden" @change="handleFilesSelected" />
          <button class="tw:btn tw:mt-2 tw:btn-secondary" @click="handleSelectFiles">Select Files</button>
        </div>
      </div>
    </div>

    <div v-else-if="isClientRole" class="tw:mx-8 tw:my-8 tw:flex tw:flex-col tw:gap-4">
      <div>
        <!-- drag and drop -->
        <div
          class="tw:mx-auto tw:w-96 tw:p-4"
          @dragover="handleFileDragOver"
          @drop="handleFileDrop"
          @dragleave="handleFileDragLeave">
          <div
            class="tw:rounded-lg tw:border-2 tw:border-dashed tw:border-gray-500 tw:p-5 tw:transition-all"
            :class="{ 'tw:bg-blue-100': draggingOver }">
            <div class="tw:flex tw:h-full tw:flex-col tw:items-center tw:justify-center">
              <CloudUpload class="tw:text-gray-500" />
              <div class="tw:text-md tw:font-bold tw:text-gray-500">Drag and drop files here</div>
              <div class="tw:text-gray-500">or</div>
              <input ref="fileInput" type="file" multiple class="tw:hidden" @change="handleFilesSelected" />

              <button class="tw:btn tw:btn-sm tw:btn-secondary" @click="handleSelectFiles">Select Files</button>
            </div>
          </div>
        </div>

        <!-- documents -->
        <div class="tw:mx-auto tw:flex tw:max-w-160 tw:flex-col tw:gap-2 tw:p-4" ref="documentsSectionRef">
          <div v-for="(document, index) in documents" :key="index" data-testid="documents">
            <div
              class="tw:flex tw:cursor-pointer tw:items-center tw:justify-between tw:gap-2 tw:rounded-lg tw:border tw:border-gray-200 tw:p-4 tw:shadow-md tw:transition-all tw:hover:bg-gray-50"
              data-testid="document">
              <CircleCheck v-if="!document.readyForUpload" :size="20" class="tw:text-gray-300" />
              <!-- wait for watcher to run -->
              <CircleCheck v-else :size="20" class="tw:text-green-500" />

              <div class="tw:flex-1 tw:overflow-hidden tw:text-base tw:text-ellipsis tw:text-gray-600 tw:italic">
                {{ document.name }}
              </div>

              <!-- client uploaded document types -->
              <AppSelect
                v-model="document.type"
                :options="
                  clientUploadedDocumentTypes.map((type) => ({
                    label: type.name,
                    value: type,
                  })).sort((a, b) => a.label.localeCompare(b.label))
                "
                class="tw:w-full"
                placeholder="Select document type"
                @update:modelValue="validateDocuments" />

              <div
                class="tw:group tw:ml-5 tw:flex tw:h-6 tw:w-6 tw:items-center tw:justify-center tw:rounded-lg tw:border tw:border-solid tw:border-gray-400 tw:transition-all tw:hover:border-red-300"
                @click="handleDeleteDocument(index)">
                <Trash2 :size="16" class="tw:text-gray-400 tw:transition-all tw:group-hover:stroke-red-500" />
              </div>
            </div>
          </div>

          <!-- form actions -->
          <div class="tw:col-span-2 tw:col-start-1 tw:row-start-1 tw:flex tw:justify-end tw:pt-3">
            <div class="tw:flex tw:justify-end tw:gap-2 tw:rounded-lg">
              <button class="tw:btn tw:bg-base-300 tw:shadow-md tw:btn-sm tw:hover:shadow-none" @click="handleCancel">
                Cancel
              </button>
              <button
                data-testid="document-upload-submit"
                class="tw:btn tw:shadow-md tw:btn-sm tw:hover:shadow-none"
                :class="{ 'tw:btn-primary': readyForUpload, 'tw:btn-disabled': !readyForUpload || uploading }"
                @click="handleUpload">
                <span :class="{'tw:loading tw:loading-dots': uploading}">Upload</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <div class="tw:grid-rows-[repeat(3, auto)] tw:grid tw:h-full tw:grid-cols-2">
        <!-- form actions -->
        <div class="tw:col-span-2 tw:col-start-1 tw:row-start-1 tw:flex tw:justify-end tw:pt-3 tw:pr-4">
          <div class="tw:flex tw:justify-end tw:gap-2 tw:rounded-lg tw:bg-gray-100/75 tw:p-3">
            <button class="tw:btn tw:bg-base-300 tw:shadow-md tw:btn-sm tw:hover:shadow-none" @click="handleCancel">
              Cancel
            </button>
            <button
              data-testid="document-upload-submit"
              class="tw:btn tw:shadow-md tw:btn-sm tw:hover:shadow-none"
              :class="{ 'tw:btn-primary': readyForUpload, 'tw:btn-disabled': !readyForUpload }"
              @click="handleUpload">
              Upload
            </button>
          </div>
        </div>

        <div class="tw:col-start-1 tw:row-span-3 tw:row-start-2">
          <!-- drag and drop -->
          <div
            class="tw:p-4 tw:pt-0"
            @dragover="handleFileDragOver"
            @drop="handleFileDrop"
            @dragleave="handleFileDragLeave">
            <div
              class="tw:rounded-lg tw:border-2 tw:border-dashed tw:border-gray-400 tw:p-5 tw:transition-all"
              :class="{ 'tw:bg-blue-100': draggingOver }">
              <div class="tw:flex tw:h-full tw:flex-col tw:items-center tw:justify-center">
                <CloudUpload class="tw:text-gray-400" />
                <div class="tw:text-md tw:font-bold tw:text-gray-400">Drag and drop files here</div>
                <div class="tw:text-gray-400">or</div>
                <input ref="fileInput" type="file" multiple class="tw:hidden" @change="handleFilesSelected" />
                <button class="tw:btn tw:btn-xs tw:btn-secondary" @click="handleSelectFiles">Select Files</button>
              </div>
            </div>
          </div>

          <!-- documents -->
          <div class="tw:flex tw:flex-col tw:gap-2 tw:p-4" ref="documentsSectionRef">
            <div v-for="(document, index) in documents" :key="index" data-testid="documents">
              <div
                class="tw:flex tw:cursor-pointer tw:items-center tw:justify-between tw:gap-2 tw:rounded-lg tw:border tw:border-gray-200 tw:p-4 tw:shadow-md tw:transition-all"
                :class="{
                  'tw:border-secondary tw:bg-blue-50 tw:shadow-none tw:hover:bg-blue-50': document.selected,
                  'tw:hover:bg-gray-50': !document.selected,
                }"
                @click="document.selected = !document.selected"
                data-testid="document">
                <OctagonAlert v-if="!document.readyForUpload" :size="20" class="tw:text-red-500" />
                <CircleCheck v-else :size="20" class="tw:text-green-500" />
                <div class="tw:flex-1 tw:overflow-hidden tw:text-base tw:text-ellipsis tw:text-gray-600 tw:italic">
                  {{ document.name }}
                </div>
                <div class="tw:flex tw:flex-col tw:items-end tw:gap-2">
                  <div v-if="document.type" class="tw:relative tw:badge tw:bg-secondary/30 tw:p-3 tw:badge-md">
                    {{ document.type.name }}
                    <CircleX
                      :size="13"
                      class="tw:absolute tw:-top-1 tw:-right-1 tw:ml-1 tw:text-red-500"
                      @click.stop="handleRemoveDocumentType(document)" />
                  </div>
                  <div v-else class="tw:text-[11px] tw:text-red-500">Select a document type...</div>
                  <div
                    v-if="document.policyId && document.policyId > 0"
                    class="tw:relative tw:badge tw:flex tw:bg-cyan-100 tw:p-3 tw:badge-md">
                    {{ document.policyNumber }}
                    <CircleX
                      :size="13"
                      class="tw:absolute tw:-top-1 tw:-right-1 tw:ml-1 tw:text-red-500"
                      @click.stop="handleRemovePolicy(document)" />
                  </div>
                  <div
                    v-else-if="document.type && isPolicyDocumentType(document.type ?? '')"
                    class="tw:text-[11px] tw:text-red-500">
                    Select a policy...
                  </div>
                </div>
                <div
                  class="tw:group tw:ml-5 tw:flex tw:h-6 tw:w-6 tw:items-center tw:justify-center tw:rounded-lg tw:border tw:border-solid tw:border-gray-400 tw:transition-all tw:hover:border-red-300"
                  @click="handleDeleteDocument(index)">
                  <Trash2 :size="16" class="tw:text-gray-400 tw:transition-all tw:group-hover:stroke-red-500" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="tw:col-start-2 tw:row-span-2 tw:row-start-2">
          <!-- document types -->
          <div class="tw:p-4 tw:pt-0" ref="documentTypesSectionRef">
            <div class="tw:pl-2 tw:text-base tw:text-gray-700">Document Types</div>
            <div
              class="tw:relative tw:flex tw:w-full tw:flex-col tw:gap-6 tw:rounded-lg tw:border tw:border-solid tw:border-gray-200 tw:bg-secondary/10 tw:p-4 tw:shadow-md">
              <div
                v-if="selectedDocuments.length === 0"
                class="tw:absolute tw:h-full tw:w-full tw:cursor-not-allowed tw:rounded-lg tw:bg-gray-100/30 tw:transition-all"></div>
              <div class="tw:flex tw:flex-col tw:gap-2">
                <div class="tw:text-sm tw:font-semibold tw:text-gray-500">Account Document Types</div>
                <div class="tw:flex tw:flex-wrap tw:gap-2 tw:pl-3">
                  <div
                    v-for="documentType in accountDocumentTypes"
                    :key="documentType.id"
                    class="tw:w-fit-content tw:cursor-pointer tw:rounded-lg tw:border tw:border-solid tw:border-gray-300 tw:p-2 tw:shadow-xs tw:transition-all"
                    :class="{
                      'tw:bg-secondary/30': documentType === selectedOptions.documentType,
                      'tw:hover:bg-gray-50': documentType !== selectedOptions.documentType,
                    }"
                    @click="handleSelectDocumentType(documentType)"
                    data-testid="account-document-type">
                    <div class="tw:text-gray-600">{{ documentType.name }}</div>
                  </div>
                </div>
              </div>
              <div class="tw:flex tw:flex-col tw:gap-2">
                <div class="tw:text-sm tw:font-semibold tw:text-gray-500">Policy Document Types</div>
                <div class="tw:flex tw:flex-wrap tw:gap-2 tw:pl-3">
                  <div
                    v-for="documentType in policyDocumentTypes"
                    :key="documentType.id"
                    class="tw:w-fit-content tw:cursor-pointer tw:rounded-lg tw:border tw:border-solid tw:border-gray-300 tw:p-2 tw:shadow-xs tw:transition-all"
                    :class="{
                      'tw:bg-secondary/30': documentType === selectedOptions.documentType,
                      'tw:hover:bg-gray-50': documentType !== selectedOptions.documentType,
                    }"
                    @click="handleSelectDocumentType(documentType)">
                    <div class="tw:text-gray-600">{{ documentType.name }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- programs & policies -->
          <div class="tw:p-4" ref="programsAndPoliciesSectionRef">
            <!-- Tabs -->
            <TabsRoot v-model="currentProgramPoliciesTab" class="tw:w-full">
              <TabsList
                class="tw:inline-flex tw:h-10 tw:items-center tw:justify-center tw:rounded-md tw:bg-slate-100 tw:p-1 tw:text-slate-600">
                <TabsTrigger
                  value="programs"
                  class="tw:inline-flex tw:items-center tw:justify-center tw:rounded-sm tw:px-3 tw:py-1.5 tw:text-sm tw:font-medium tw:whitespace-nowrap tw:ring-offset-white tw:transition-all tw:focus-visible:ring-2 tw:focus-visible:ring-slate-950 tw:focus-visible:ring-offset-2 tw:focus-visible:outline-none tw:disabled:pointer-events-none tw:disabled:opacity-50 tw:data-[state=active]:bg-white tw:data-[state=active]:text-slate-950 tw:data-[state=active]:shadow-sm"
                  >Program & Policies</TabsTrigger
                >
                <TabsTrigger
                  value="policies"
                  class="tw:inline-flex tw:items-center tw:justify-center tw:rounded-sm tw:px-3 tw:py-1.5 tw:text-sm tw:font-medium tw:whitespace-nowrap tw:ring-offset-white tw:transition-all tw:focus-visible:ring-2 tw:focus-visible:ring-slate-950 tw:focus-visible:ring-offset-2 tw:focus-visible:outline-none tw:disabled:pointer-events-none tw:disabled:opacity-50 tw:data-[state=active]:bg-white tw:data-[state=active]:text-slate-950 tw:data-[state=active]:shadow-sm"
                  >Policies</TabsTrigger
                >
              </TabsList>

              <TabsContent value="programs">
                <div
                  class="tw:flex tw:w-full tw:flex-col tw:justify-between tw:rounded-lg tw:border tw:border-solid tw:border-gray-200 tw:bg-cyan-50/50 tw:p-4 tw:shadow-md">
                  <div
                    v-if="selectedDocuments.length === 0"
                    class="tw:h-full tw:w-full tw:cursor-not-allowed tw:rounded-lg tw:bg-gray-100/30 tw:transition-all"></div>

                  <!-- Show Current Only toggle -->
                  <label class="tw:label tw:w-fit tw:gap-1">
                    <span class="tw:label-text tw:text-[11px] tw:text-gray-600">Show Current Only</span>
                    <input
                      type="checkbox"
                      :checked="showCurrentProgramsOnly"
                      v-model="showCurrentProgramsOnly"
                      class="tw:toggle tw:opacity-80 tw:toggle-primary tw:toggle-sm" />
                  </label>

                  <!-- programs -->
                  <div class="tw:flex">
                    <div class="tw:flex tw:w-1/2 tw:flex-col tw:gap-1 tw:p-1">
                      <div v-if="!programsLoaded" class="tw:h-96 tw:w-full tw:skeleton"></div>
                      <div
                        v-else
                        v-for="program in programs"
                        :key="program.program.id"
                        class="tw:flex tw:cursor-pointer tw:flex-col tw:justify-between tw:rounded-lg tw:border tw:border-solid tw:border-gray-200 tw:p-2 tw:shadow-md"
                        :class="{
                          'tw:border-blue-100 tw:bg-blue-50 tw:shadow-none':
                            program.program.id === selectedOptions.programId,
                          'tw:hover:bg-gray-50': program.program.id !== selectedOptions.programId,
                        }"
                        @click="handleSelectProgram(program.program.id)"
                        data-testid="program">
                        <div class="tw:text-gray-600">{{ program.program.name }}</div>
                        <div class="tw:flex tw:flex-wrap tw:justify-between">
                          <div class="tw:text-[11px] tw:text-gray-600">{{ program.program.type }}</div>
                          <div class="tw:flex tw:flex-col tw:items-end tw:gap-1">
                            <div
                              v-for="coverageType in program.program.coverageTypes"
                              :key="coverageType"
                              class="tw:w-fit-content tw:rounded-box tw:bg-[#4E738A] tw:px-1 tw:text-[11px] tw:font-semibold tw:text-gray-100">
                              {{ coverageType }}
                            </div>
                          </div>
                        </div>
                        <div>
                          <div class="tw:text-[11px] tw:text-gray-600">{{ program.policies.length }} Policies</div>
                        </div>
                      </div>
                    </div>
                    <div class="tw:divider tw:m-0 tw:divider-horizontal tw:p-1">
                      <ChevronsRight :size="28" />
                    </div>
                    <!-- policies -->
                    <div class="tw:flex tw:w-1/2 tw:flex-col tw:gap-1 tw:p-1">
                      <div
                        v-for="policy in policiesFromSelectedProgram"
                        :key="policy.policyID"
                        class="tw:flex tw:w-full tw:cursor-pointer tw:flex-col tw:items-center tw:justify-between tw:rounded-lg tw:border tw:border-solid tw:border-gray-200 tw:p-2 tw:shadow-md"
                        :class="{
                          'tw:border-blue-100 tw:bg-blue-50 tw:shadow-none':
                            Number(policy.uniqPolicy) === selectedOptions.policyId,
                          'tw:hover:bg-gray-50': Number(policy.uniqPolicy) !== selectedOptions.policyId,
                        }"
                        @click="handleSelectPolicy(policy)"
                        data-testid="policy">
                        <div class="tw:w-full tw:text-gray-600">{{ policy.description }}</div>
                        <div class="tw:flex tw:w-full tw:flex-col tw:justify-between">
                          <div class="tw:text-[11px] tw:text-gray-600">Carrier: {{ policy.carrierGroup }}</div>
                          <div class="tw:text-[11px] tw:text-gray-600">Coverage Type: {{ policy.coverageType }}</div>
                          <div class="tw:text-[11px] tw:text-gray-600">Policy Number: {{ policy.policyID }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="policies">
                <div
                  class="tw:relative tw:rounded-lg tw:border tw:border-solid tw:border-gray-200 tw:bg-cyan-50/50 tw:p-4 tw:shadow-md">
                  <!-- Controls row with toggle and search -->
                  <div class="tw:mb-3 tw:flex tw:items-center tw:gap-4">
                    <!-- Show Current Only toggle -->
                    <label class="tw:label tw:w-fit tw:gap-1">
                      <span class="tw:label-text tw:text-[11px] tw:text-gray-600">Show Current Only</span>
                      <input
                        type="checkbox"
                        :checked="showCurrentPoliciesOnly"
                        v-model="showCurrentPoliciesOnly"
                        class="tw:toggle tw:border-solid tw:opacity-80 tw:toggle-primary tw:toggle-sm" />
                    </label>

                    <!-- Search bar -->
                    <div class="tw:relative tw:flex-1">
                      <input
                        v-model="policySearchQuery"
                        type="text"
                        placeholder="Search policies by name, number, carrier, or coverage type..."
                        class="tw:input tw:input-sm tw:w-full tw:bg-white tw:pr-8" />
                      <button
                        v-if="policySearchQuery"
                        @click="policySearchQuery = ''"
                        class="tw:absolute tw:top-1/2 tw:right-2 tw:-translate-y-1/2 tw:text-gray-400 tw:transition-colors tw:hover:text-gray-600">
                        <X :size="16" />
                      </button>
                    </div>
                  </div>

                  <!-- All policies list -->
                  <div class="tw:flex tw:flex-col tw:gap-1">
                    <div v-if="allAccountPoliciesLoading" class="tw:h-96 tw:w-full tw:skeleton"></div>
                    <div v-else-if="allAccountPolicies.length === 0" class="tw:py-4 tw:text-center tw:text-gray-500">
                      No policies found for this account
                    </div>
                    <div
                      v-else-if="filteredAccountPolicies.length === 0"
                      class="tw:py-4 tw:text-center tw:text-gray-500">
                      No policies match your search
                    </div>
                    <div v-else class="tw:grid tw:grid-cols-2 tw:gap-2">
                      <div
                        v-for="policy in filteredAccountPolicies"
                        :key="policy.policyID"
                        class="tw:flex tw:cursor-pointer tw:flex-col tw:items-center tw:justify-between tw:rounded-lg tw:border tw:border-solid tw:border-gray-200 tw:p-2 tw:shadow-md"
                        :class="{
                          'tw:border-blue-100 tw:bg-blue-50 tw:shadow-none':
                            Number(policy.uniqPolicy) === selectedOptions.policyId,
                          'tw:hover:bg-gray-50': Number(policy.uniqPolicy) !== selectedOptions.policyId,
                        }"
                        @click="handleSelectPolicy(policy)"
                        data-testid="all-policy">
                        <div
                          class="tw:w-full tw:text-gray-600"
                          v-html="highlightText(policy.description, policySearchQuery)"></div>
                        <div class="tw:flex tw:w-full tw:flex-col tw:justify-between">
                          <div class="tw:text-[11px] tw:text-gray-600">
                            Carrier: <span v-html="highlightText(policy.carrierGroup, policySearchQuery)"></span>
                          </div>
                          <div class="tw:text-[11px] tw:text-gray-600">
                            Coverage Type: <span v-html="highlightText(policy.coverageType, policySearchQuery)"></span>
                          </div>
                          <div class="tw:text-[11px] tw:text-gray-600">
                            Policy Number: <span v-html="highlightText(policy.policyID, policySearchQuery)"></span>
                          </div>
                          <div
                            v-if="policy.effectiveDate && policy.expirationDate"
                            class="tw:text-[11px] tw:text-gray-600">
                            Effective: {{ new Date(policy.effectiveDate).toLocaleDateString() }} -
                            {{ new Date(policy.expirationDate).toLocaleDateString() }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </TabsRoot>

            <!-- All Policies Tab Content -->
          </div>

          <!-- tags -->
          <div class="tw:p-4" ref="tagsSectionRef">
            <div class="tw:pl-2 tw:text-base tw:text-gray-700">Tags</div>
            <div
              class="tw:relative tw:flex tw:w-full tw:flex-col tw:gap-4 tw:rounded-lg tw:border tw:border-solid tw:border-gray-200 tw:bg-purple-50/30 tw:p-4 tw:shadow-md">
              <div
                v-if="selectedDocuments.length === 0"
                class="tw:absolute tw:h-full tw:w-full tw:cursor-not-allowed tw:rounded-lg tw:bg-gray-100/30 tw:transition-all"></div>
              
              <div class="tw:flex tw:flex-col">
                <label class="tw:label">
                  <span class="tw:label-text tw:text-xs tw:text-gray-500 tw:pb-1">Add tags to documents</span>
                </label>

                <div v-if="selectedTags.length > 0" class="tw:flex tw:flex-wrap tw:pb-2">
                  <div
                    v-for="tag in selectedTags"
                    :key="tag.value"
                    @click.stop="handleTagRemove(tag)"
                    class="tw:group tw:badge tw:flex tw:h-fit tw:cursor-pointer tw:gap-1 tw:badge-ghost tw:badge-xs tw:hover:bg-red-100">
                    <div class="tw:p-1">{{ tag.label }}</div>
                    <X :size="12" class="tw:group-hover:stroke-red-600" />
                  </div>
                </div>

                <Combobox
                  :key="tagsComboboxKey"
                  v-model="selectedTag"
                  input-class="tw:input-sm"
                  :options="availableTagsFiltered"
                  placeholder="Add tags..."
                  :allow-free-text="true"
                  @selected="handleTagSelect" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
